import React from 'react';
import { Flex, type FlexProps, VStack } from '@chakra-ui/react';

/**
 * How much of the window a page is allowed.
 *
 * `reading` is the default and the right answer for most of the app: a column of text or cards is
 * hard to read past about 800px, so the page stays narrow and centred however wide the window is.
 *
 * `wide` is for a page that puts something *beside* the main column — a filter panel, a second pane.
 * Those need room the reading width cannot give, and squeezing them into it leaves the main column
 * at half its usable size instead. Still capped, because "full width" on a 27-inch monitor is not a
 * layout.
 */
export const PageWidths = {
	Reading: 'reading',
	Wide: 'wide',
} as const;
export type PageWidth = (typeof PageWidths)[keyof typeof PageWidths];

const WIDTH: Record<PageWidth, FlexProps['w']> = {
	reading: { base: 'full', md: '600px', lg: '800px' },
	wide: 'full',
};

/**
 * A wide page is capped but not centred on desktop.
 *
 * Centring is what leaves the gutter: at the reading width the page sits in the middle of the window
 * with empty space either side, which is right for a single column and wrong for one that has a
 * panel beside it — the panel ends up floating in the middle of the screen with room going spare to
 * its left. Anchored to the left instead, the way the nav beside it already is.
 *
 * Still capped, because a list of task rows stretched across a 27-inch monitor is not a layout.
 */
const MAX_WIDTH: Record<PageWidth, FlexProps['maxW']> = {
	reading: undefined,
	wide: { base: 'full', xl: '1440px' },
};

const MARGIN: Record<PageWidth, FlexProps['mx']> = {
	reading: 'auto',
	wide: { base: 'auto', lg: 0 },
};

interface PageContentProps {
	children: React.ReactNode;
	pt?: FlexProps['pt'];
	/** Defaults to the reading column; pass `wide` for a page with something alongside it. */
	width?: PageWidth;
	/** Optional cap for a particular wide workspace without changing the shared wide-page default. */
	maxW?: FlexProps['maxW'];
	/** Centre a wide page as one workspace instead of anchoring it beside the app navigation. */
	centerWide?: boolean;
}

/**
 * Reusable page content layout component
 * Provides consistent spacing, width constraints, and centering for page content
 */
const PageContent: React.FC<PageContentProps> = ({
	children,
	pt,
	width = PageWidths.Reading,
	maxW,
	centerWide = false,
}) => {
	return (
		<Flex className="page-content-scrollable" flex="1" w="100%" pt={pt} pb={6} px={{ base: 4, md: 6 }}>
			<VStack
				gap={8}
				w={WIDTH[width]}
				maxW={maxW ?? MAX_WIDTH[width]}
				mx={width === PageWidths.Wide && centerWide ? 'auto' : MARGIN[width]}
				align="center"
			>
				{children}
			</VStack>
		</Flex>
	);
};

export default PageContent;
